import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { practitionerService } from '../../../services/practitionerService';

const AccessLinkModal = ({ practitioner, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const publicUrl = practitionerService?.getPublicUrl(practitioner?.slug);
  const dashboardUrl = `${window.location?.origin}/individual-practitioner-dashboard`;

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset?.charAt(Math.floor(Math.random() * charset?.length));
    }
    return password;
  };

  const handleGenerateCredentials = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let password = generateRandomPassword();
      const email = `${practitioner?.slug}@practitioner.endoreferral.fr`;
      const fullName = `${practitioner?.practitionerTitle} ${practitioner?.practitionerName}`;

      const result = await practitionerService?.createUserAccount(
        email,
        password,
        fullName,
        practitioner?.id
      );

      if (result?.user) {
        setGeneratedCredentials({
          email,
          password,
          dashboardUrl,
          createdAt: new Date()?.toISOString()
        });
        setSuccess('Identifiants générés avec succès!');
      }
    } catch (err) {
      console.error('Error generating credentials:', err);
      setError(err?.message || 'Erreur lors de la génération des identifiants');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator?.clipboard?.writeText(text);
    setSuccess('Copié dans le presse-papiers!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `qrcode-${practitioner?.slug}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const sendEmailNotification = () => {
    if (!generatedCredentials) return;

    const subject = encodeURIComponent('Vos identifiants de connexion - EndoReferral');
    const body = encodeURIComponent(
      `Bonjour ${practitioner?.practitionerTitle} ${practitioner?.practitionerName},\n\n` +
      `Vos identifiants de connexion pour votre tableau de bord EndoReferral :\n\n` +
      `URL de connexion : ${generatedCredentials?.dashboardUrl}\n` +
      `Email : ${generatedCredentials?.email}\n` +
      `Mot de passe : ${generatedCredentials?.password}\n\n` +
      `Votre lien public d'adressage : ${publicUrl}\n\n` +
      `Cordialement,\n` +
      `L'équipe EndoReferral`
    );

    window.location.href = `mailto:${practitioner?.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/8">
        {/* Header */}
        <div className="sticky top-0 bg-card-elevated/95 backdrop-blur-xl border-b border-white/8 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/20 rounded-xl">
              <Icon name="Link" size={24} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-primary">Liens d'Accès & QR Code</h2>
              <p className="text-sm text-muted">{practitioner?.practiceName}</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            className="p-2 text-muted hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Alerts */}
          {error && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{success}</p>
            </div>
          )}

          {/* Public Referral Link & QR Code */}
          <div className="bg-card-elevated rounded-2xl p-6 space-y-4 border border-white/8">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="QrCode" size={20} className="text-accent" />
              Lien Public d'Adressage
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* QR Code */}
              <div className="flex flex-col items-center justify-center bg-white rounded-xl p-6 shadow-sm">
                <QRCodeSVG
                  id="qr-code-svg"
                  value={publicUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Scannez pour accéder au formulaire d'adressage
                </p>
                <Button
                  onClick={downloadQRCode}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Icon name="Download" size={16} />
                  Télécharger QR Code
                </Button>
              </div>

              {/* URL */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-secondary mb-2 block">
                    URL à partager avec les dentistes
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={publicUrl}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(publicUrl)}
                      className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                      title="Copier"
                    >
                      <Icon name="Copy" size={18} />
                    </button>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/30 rounded-lg p-3">
                  <p className="text-xs text-secondary">
                    <strong className="text-primary">Usage:</strong> Partagez ce lien ou ce QR code avec les dentistes pour qu'ils puissent vous adresser des patients directement.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Practitioner Dashboard Credentials */}
          <div className="border-t border-white/8 pt-6 space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Icon name="Key" size={20} className="text-accent" />
              Identifiants du Tableau de Bord
            </h3>

            {!generatedCredentials ? (
              <div className="bg-card-elevated rounded-lg p-6 text-center space-y-4 border border-white/8">
                <Icon name="Lock" size={48} className="text-muted mx-auto" />
                <div>
                  <p className="text-sm text-secondary mb-2">
                    Générez des identifiants de connexion pour permettre au praticien d'accéder à son tableau de bord.
                  </p>
                  <p className="text-xs text-muted">
                    Un email et un mot de passe sécurisé seront créés automatiquement.
                  </p>
                </div>
                <Button
                  onClick={handleGenerateCredentials}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Génération...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Key" size={18} />
                      <span>Générer les Identifiants</span>
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-warning-bg border border-warning/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-secondary">
                      <strong className="text-primary">Important:</strong> Ces identifiants ont été générés. Transmettez-les au praticien de manière sécurisée.
                    </p>
                  </div>
                </div>

                <div className="bg-card-elevated rounded-lg p-4 space-y-3 border border-white/8">
                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">URL du tableau de bord</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={generatedCredentials?.dashboardUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.dashboardUrl)}
                        className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                        title="Copier"
                      >
                        <Icon name="Copy" size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">Email de connexion</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={generatedCredentials?.email}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.email)}
                        className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                        title="Copier"
                      >
                        <Icon name="Copy" size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">Mot de passe</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={generatedCredentials?.password}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm font-mono"
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                        title={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                      </button>
                      <button
                        onClick={() => copyToClipboard(generatedCredentials?.password)}
                        className="p-2 text-muted hover:text-accent hover:bg-card-hover rounded-lg transition-colors"
                        title="Copier"
                      >
                        <Icon name="Copy" size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={sendEmailNotification}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-success hover:bg-success/90 text-white rounded-lg font-medium transition-colors"
                >
                  <Icon name="Mail" size={18} />
                  Envoyer par Email
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessLinkModal;